/**
 * Self-attribution ledger for the stale-paragraph guard.
 *
 * THE PROBLEM THIS SOLVES (the single copy of this rationale — link here rather
 * than restating it):
 *
 * The stale-paragraph guard (CS-481) asks "has this paragraph changed since the
 * BE snapshot?" but cannot tell a user's manual Word edit from a sibling card's
 * edit that we just applied ourselves. Contract Space routinely emits an EDIT on
 * pN plus an INSERT/DELETE anchored to the same pN, both generated from the same
 * snapshot; applying the edit invalidated the sibling's snapshot and blocked it
 * as "Apply Unavailable". This ledger lets the guard accept our own mutation
 * while still blocking the user's.
 *
 * INSERT/DELETE resolve their target by index and never read the paragraph's
 * text, so our own edit does not invalidate them. EDIT is deliberately excluded
 * from self-attribution: its searchPhrase occurrence ordinals were counted
 * against the pre-edit snapshot, so a second edit on an already-edited paragraph
 * is genuinely unsafe and stays blocked.
 *
 * The recorded text is OBSERVED, never predicted: under track changes
 * paragraph.text carries both struck and inserted runs, so it cannot be derived
 * from the edit's replacementText.
 *
 * Anchors are registered off the TOOL-RESULT STREAM rather than off the rendered
 * cards, because previews are grouped into separate EditPreviewLists by position
 * in the content stream — a sibling edit and insert land in DIFFERENT lists
 * whenever the model narrates between the two tool calls, so a component-tree
 * gate would miss them nondeterministically.
 *
 * Lifetime is owned by wordSequentialEditService's clear functions, which call
 * clearAppliedParagraphLedger() alongside clearing the index tracker: all of it
 * describes "what we have applied, or are about to apply, since the last user
 * message". This module never imports that one — the dependency runs one way.
 */

/* global Word */

import { AutoRedlineOutput } from "../../../interfaces/autoRedlineTypes";
import { normalizeParagraphText } from "../../../helpers/word";
import { createLogger } from "../../../utils/logger";
import { UnifiedIndexTracker } from "./wordIndexAdjustmentTracker";

const log = createLogger("AppliedParagraphLedger");

/**
 * Observed, whitespace-normalized text of each paragraph AFTER a redline apply
 * of ours mutated it.
 *
 * Keyed by OWNING MESSAGE as well as paragraphId. Scoping to the message is what
 * stops an edit applied in the current turn from validating a stale
 * INSERT/DELETE card left unapplied in an earlier turn: every card in history
 * keeps a live Apply button, so with a bare paragraphId key an entry written for
 * message B would silently un-block message A's card, applying superseded
 * content at an index resolved by a tracker that was reset in between.
 */
const appliedParagraphTextLedger = new Map<string, string>();

/**
 * Keys an INSERT or DELETE in the current response is anchored to — i.e. the
 * only paragraphs whose post-apply text anyone will ever read back out.
 */
const baselineAnchors = new Set<string>();

/**
 * PRIVATE, and deliberately so: an exported key builder would be a second thing
 * every layer has to use identically. With it private, a caller cannot construct
 * a malformed key. paragraphId is a number, so the last colon always separates
 * the two parts even if a messageId contains one.
 */
function ledgerKey(messageId: string, paragraphId: number): string {
  return `${messageId}:${paragraphId}`;
}

/**
 * Records that an INSERT or DELETE in this response is anchored to a paragraph,
 * so an EDIT applied to the same paragraph knows to capture a post-apply
 * baseline for it. A no-op for anything else — this is the ONLY place that
 * decides which operation types anchor.
 *
 * Called once per auto-redline tool result as it streams in, before any apply
 * can happen (Apply is disabled while streaming).
 */
export function registerBaselineAnchor(output: AutoRedlineOutput, messageId: string): void {
  const { paragraphId, operationType } = output;
  if (paragraphId === undefined) return;
  if (operationType !== "insert" && operationType !== "delete") return;

  baselineAnchors.add(ledgerKey(messageId, paragraphId));
}

/**
 * Whether any INSERT/DELETE in this message is anchored to this paragraph, i.e.
 * whether capturing a post-apply baseline for it can ever pay off. Pure-EDIT
 * responses answer false for every paragraph and skip the extra Word reads.
 */
export function needsAppliedTextBaseline(messageId: string, paragraphId: number): boolean {
  return baselineAnchors.has(ledgerKey(messageId, paragraphId));
}

/**
 * The normalized text this paragraph had after our own apply mutated it, or
 * undefined if we never mutated it under this message (or the capture failed).
 */
export function getAppliedParagraphText(
  messageId: string,
  paragraphId: number
): string | undefined {
  return appliedParagraphTextLedger.get(ledgerKey(messageId, paragraphId));
}

/** Drops every entry. See the module header for why this shares the tracker's lifetime. */
export function clearAppliedParagraphLedger(): void {
  appliedParagraphTextLedger.clear();
  baselineAnchors.clear();
}

/**
 * Capture the post-apply text of a paragraph we just mutated, so the stale guard
 * on a sibling INSERT/DELETE card anchored to the same paragraph can recognise
 * the change as ours rather than the user's.
 *
 * Resolves the live index through the same tracker translation
 * readLiveParagraphText uses, so the value written here is comparable to the
 * value the guard reads back.
 *
 * Reuses the caller's open request context — this is two extra syncs inside an
 * existing Word.run, not a new round-trip.
 */
export async function recordAppliedParagraphText(
  context: Word.RequestContext,
  params: { messageId: string; paragraphId: number; indexTracker?: UnifiedIndexTracker }
): Promise<void> {
  const { messageId, paragraphId, indexTracker } = params;
  const adjustedIndex = indexTracker
    ? indexTracker.calculateAdjustedIndexForEdit(paragraphId)
    : paragraphId;

  const paragraphs = context.document.body.paragraphs;
  paragraphs.load("items");
  await context.sync();

  if (adjustedIndex < 0 || adjustedIndex >= paragraphs.items.length) {
    log.warn("Could not capture applied paragraph text — adjusted index out of bounds", {
      paragraphId,
      adjustedIndex,
      paragraphCount: paragraphs.items.length,
    });
    return;
  }

  const paragraph = paragraphs.items[adjustedIndex];
  paragraph.load("text");
  await context.sync();

  // Never record an empty baseline. normalizeParagraphText collapses "", "\r" and
  // "   " to the same string, so an empty entry would match ANY blank paragraph —
  // and if the tracker is desynced (e.g. the user undid an applied insert) the
  // guard reads the wrong paragraph, very often a blank one. That would turn a
  // safe block into a false accept.
  const normalized = normalizeParagraphText(paragraph.text);
  if (!normalized) return;

  appliedParagraphTextLedger.set(ledgerKey(messageId, paragraphId), normalized);

  log.debug("Captured applied paragraph text for self-attribution", {
    paragraphId,
    adjustedIndex,
    length: normalized.length,
  });
}
